import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { authService } from '../../services/authService';
import { helpContentService } from '../../services/helpContentService';
import { usePageTitle } from '../../hooks/usePageTitle';
import { Logo } from '../../hooks/useLogo';

const Register = () => {
  usePageTitle('Register');
  const navigate = useNavigate();
  const { register, loading, error, clearError } = useAuth();

  // form steps
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  // registration step: form | verification | success
  const [registrationStep, setRegistrationStep] = useState(
    () => localStorage.getItem('registrationStep') || 'form'
  );

  // form data
  const [formData, setFormData] = useState({
    orgName: '',
    orgDescription: '',
    orgCategoryId: '',
    fullName: '', // Admin's full name
    username: '',
    email: '',
    countryId: '', // For phone dial code (also determines org country_code)
    phone: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false
  });

  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // dropdowns and search states
  const [orgCategories, setOrgCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [categorySearch, setCategorySearch] = useState('');
  const [countrySearch, setCountrySearch] = useState(''); // Phone dial code country search
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingCountries, setLoadingCountries] = useState(false);

  // refs for dropdown outside click detection
  const categoryRef = useRef(null);
  const countryRef = useRef(null);

  // Terms of Service / Privacy Policy links, from Branding & Legal settings
  const [legalLinks, setLegalLinks] = useState({ terms_url: '', privacy_policy_url: '' });

  // on mount restore saved email (if verification persisted)
  useEffect(() => {
    const savedEmail = localStorage.getItem('registrationEmail');
    if (savedEmail) {
      setFormData(prev => ({ ...prev, email: savedEmail }));
    }
  }, []);

  // fetch Terms/Privacy links from Branding & Legal settings (public endpoint)
  useEffect(() => {
    const fetchLegalLinks = async () => {
      try {
        const response = await helpContentService.getAppSettings();
        const settings = response?.settings || [];
        const find = (key) => settings.find((s) => s.setting_key === key)?.setting_value || '';
        setLegalLinks({
          terms_url: find('terms_url'),
          privacy_policy_url: find('privacy_policy_url')
        });
      } catch (err) {
        console.error('Error fetching legal links:', err);
      }
    };
    fetchLegalLinks();
  }, []);

  // persist registrationStep to localStorage
  useEffect(() => {
    localStorage.setItem('registrationStep', registrationStep);
  }, [registrationStep]);

  // fetch categories/countries on mount
  useEffect(() => {
    fetchOrgCategories();
    fetchCountries();
  }, []);

  // close dropdowns when clicking outside
  useEffect(() => {
    const onDocClick = (e) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target)) {
        setShowCategoryDropdown(false);
      }
      if (countryRef.current && !countryRef.current.contains(e.target)) {
        setShowCountryDropdown(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // ---------- API fetchers ----------
  const fetchOrgCategories = async (search = '') => {
    setLoadingCategories(true);
    try {
      const response = await authService.getOrganizationCategories(search);
      setOrgCategories(response.categories || []);
    } catch (err) {
      console.error('Error fetching organization categories:', err);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchCountries = async (search = '') => {
    setLoadingCountries(true);
    try {
      const response = await authService.getCountriesWithDialCodes(search);
      setCountries(response.countries || []);
    } catch (err) {
      console.error('Error fetching countries:', err);
    } finally {
      setLoadingCountries(false);
    }
  };

  // ---------- search handlers (debounced) ----------
  const handleCategorySearch = (value) => {
    setCategorySearch(value);
    setFormData(prev => ({ ...prev, orgCategoryId: '' }));

    clearTimeout(window.categorySearchTimeout);
    window.categorySearchTimeout = setTimeout(() => {
      fetchOrgCategories(value);
    }, 300);

    setShowCategoryDropdown(true);
  };

  const handleCountrySearch = (value) => {
    setCountrySearch(value);
    setFormData(prev => ({ ...prev, countryId: '' }));

    clearTimeout(window.countrySearchTimeout);
    window.countrySearchTimeout = setTimeout(() => {
      fetchCountries(value);
    }, 300);

    setShowCountryDropdown(true);
  };

  // ---------- select handlers ----------
  const selectCategory = (category) => {
    setFormData(prev => ({ ...prev, orgCategoryId: category.id }));
    setCategorySearch(category.category_name);
    setShowCategoryDropdown(false);

    if (formErrors.orgCategoryId) {
      setFormErrors(prev => ({ ...prev, orgCategoryId: '' }));
    }
  };

  const selectCountry = (country) => {
    setFormData(prev => ({ ...prev, countryId: country.id }));
    setCountrySearch(country.dial_code || `${country.country_code || ''} ${country.dial_code || ''}`);
    setShowCountryDropdown(false);

    if (formErrors.countryId) {
      setFormErrors(prev => ({ ...prev, countryId: '' }));
    }
  };

  // ---------- input change ----------
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }

    if (error && typeof clearError === 'function') {
      clearError();
    }
  };

  // ---------- validation ----------
  const validateStep = (step) => {
    const errors = {};

    switch (step) {
      case 1:
        if (!formData.orgCategoryId) {
          errors.orgCategoryId = 'Organization category is required';
        }
        if (!formData.orgName.trim()) {
          errors.orgName = 'Organization name is required';
        }
        break;

      case 2:
        if (!formData.fullName.trim()) {
          errors.fullName = 'Full name is required';
        } else if (formData.fullName.trim().length < 3) {
          errors.fullName = 'Full name must be at least 3 characters';
        }
        if (!formData.username.trim()) {
          errors.username = 'Username is required';
        } else if (formData.username.length < 3) {
          errors.username = 'Username must be at least 3 characters';
        }
        if (!formData.email.trim()) {
          errors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
          errors.email = 'Email is invalid';
        }
        if (!formData.countryId) {
          errors.countryId = 'Country is required';
        }
        if (!formData.phone.trim()) {
          errors.phone = 'Phone number is required';
        } else if (!/^\d{7,15}$/.test(formData.phone.replace(/\s/g, ''))) {
          errors.phone = 'Phone number must be 7-15 digits';
        }
        break;

      case 3:
        if (!formData.password.trim()) {
          errors.password = 'Password is required';
        } else if (formData.password.length < 6) {
          errors.password = 'Password must be at least 6 characters';
        }
        if (!formData.confirmPassword.trim()) {
          errors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
          errors.confirmPassword = 'Passwords do not match';
        }
        if (!formData.agreeToTerms) {
          errors.agreeToTerms = 'You must agree to the terms and conditions';
        }
        break;

      default:
        break;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // ---------- submit ----------
  const handleSubmit = async (e) => {
    e.preventDefault(); // <-- FIX: prevent reload immediately

    if (!validateStep(3)) {
      return;
    }

    try {
      const payload = {
        org_name: formData.orgName,
        description: formData.orgDescription,
        org_category_id: formData.orgCategoryId,
        full_name: formData.fullName,
        username: formData.username,
        email: formData.email,
        country_id: formData.countryId, // Backend will derive org country_code from this
        phone: formData.phone,
        password: formData.password
      };

      console.log('Submitting registration data:', payload);

      const result = await register(payload);
      console.log('Registration result:', result);

      // Accept a few shapes just in case
      const success = result && (result.success === true || result.status === 'success' || result.data?.status === 'success');

      if (success) {
        // save email so verification page can survive refresh
        localStorage.setItem('registrationEmail', payload.email);

        // switch to verification or success
        if (result.verification_required || result.requires_verification) {
          console.log('Registration successful, showing verification page');
          setRegistrationStep('verification');
          localStorage.setItem('registrationStep', 'verification');
        } else {
          setRegistrationStep('success');
          localStorage.setItem('registrationStep', 'success');
        }
      } else {
        const message = result?.message || 'Registration failed';
        setFormErrors(prev => ({ ...prev, general: message }));
        console.warn('Unexpected registration response:', result);
      }
    } catch (err) {
      console.error('Registration error:', err);
      setFormErrors(prev => ({ ...prev, general: 'An error occurred during registration' }));
    }
  };

  // --------- resend verification ----------
  const handleResendVerification = async () => {
    const emailToUse = formData.email || localStorage.getItem('registrationEmail');
    if (!emailToUse) {
      alert('No email available to resend verification.');
      return;
    }

    try {
      // try authService if available
      if (authService && typeof authService.resendVerification === 'function') {
        const resp = await authService.resendVerification({ email: emailToUse });
        if (resp && (resp.success || resp.status === 'success')) {
          alert('Verification email sent successfully!');
        } else {
          alert(resp?.message || 'Failed to send verification email');
        }
      } else {
        // fallback endpoint
        const response = await fetch('/api/resend-verification', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: emailToUse })
        });
        const data = await response.json();
        if (response.ok) {
          alert('Verification email sent successfully!');
        } else {
          alert(data.message || 'Failed to send verification email');
        }
      }
    } catch (err) {
      console.error('Error resending verification:', err);
      alert('Failed to send verification email');
    }
  };

  // --------- navigate to login (clear persisted registration state) ----------
  const handleGoToLogin = () => {
    localStorage.removeItem('registrationStep');
    localStorage.removeItem('registrationEmail');
    setRegistrationStep('form');
    navigate('/login');
  };

  const getSelectedCategory = () => {
    return orgCategories.find(cat => cat.id === parseInt(formData.orgCategoryId)) || null;
  };

  const getSelectedCountry = () => {
    return countries.find(country => country.id === parseInt(formData.countryId)) || null;
  };

  // --------- render step content (keeps your original layout) ----------
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="form-section active">
            <h3>Organization Information</h3>

            <div className="form-group">
              <label htmlFor="orgCategory">Organization Category *</label>
              <div className="dropdown-container" ref={categoryRef}>
                <input
                  type="text"
                  value={categorySearch}
                  onChange={(e) => handleCategorySearch(e.target.value)}
                  onFocus={() => setShowCategoryDropdown(true)}
                  className={formErrors.orgCategoryId ? 'form-input error' : 'form-input'}
                  placeholder="Search and select organization category"
                  autoComplete="off"
                />
                {showCategoryDropdown && (
                  <div className="dropdown-menu">
                    {loadingCategories ? (
                      <div className="dropdown-item loading">
                        <div className="spinner"></div>
                        Loading categories...
                      </div>
                    ) : orgCategories.length > 0 ? (
                      orgCategories.map(category => (
                        <div
                          key={category.id}
                          className="dropdown-item"
                          onClick={() => selectCategory(category)}
                        >
                          {category.category_name}
                        </div>
                      ))
                    ) : (
                      <div className="dropdown-item no-results">
                        No categories found
                      </div>
                    )}
                  </div>
                )}
              </div>
              {formErrors.orgCategoryId && (
                <div className="error-message">{formErrors.orgCategoryId}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="orgName">Organization Name *</label>
              <input
                type="text"
                id="orgName"
                name="orgName"
                value={formData.orgName}
                onChange={handleChange}
                className={formErrors.orgName ? 'form-input error' : 'form-input'}
                placeholder="Enter your organization name"
                required
              />
              {formErrors.orgName && (
                <div className="error-message">{formErrors.orgName}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="orgDescription">Organization Description</label>
              <textarea
                id="orgDescription"
                name="orgDescription"
                value={formData.orgDescription}
                onChange={handleChange}
                className="form-input"
                placeholder="Brief description of your organization (optional)"
                rows="4"
              />
            </div>

            <button type="button" className="btn" onClick={nextStep}>
              Next Step
            </button>
          </div>
        );

      case 2:
        return (
          <div className="form-section active">
            <h3>Administrator Details</h3>

            <div className="form-group">
              <label htmlFor="fullName">Full Name *</label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                className={formErrors.fullName ? 'form-input error' : 'form-input'}
                placeholder="Enter your full name"
                required
              />
              {formErrors.fullName && (
                <div className="error-message">{formErrors.fullName}</div>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="username">Username *</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  className={formErrors.username ? 'form-input error' : 'form-input'}
                  placeholder="Choose a username"
                  required
                />
                {formErrors.username && (
                  <div className="error-message">{formErrors.username}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={formErrors.email ? 'form-input error' : 'form-input'}
                  placeholder="Enter your email address"
                  required
                />
                {formErrors.email && (
                  <div className="error-message">{formErrors.email}</div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone Number *</label>
              <div className="phone-input-container">
                <div className="country-selector" ref={countryRef}>
                  <input
                    type="text"
                    value={countrySearch}
                    onChange={(e) => handleCountrySearch(e.target.value)}
                    onFocus={() => setShowCountryDropdown(true)}
                    className={formErrors.countryId ? 'form-input error country-input' : 'form-input country-input'}
                    placeholder="Select country"
                    autoComplete="off"
                  />
                  {showCountryDropdown && (
                    <div className="dropdown-menu">
                      {loadingCountries ? (
                        <div className="dropdown-item loading">
                          <div className="spinner"></div>
                          Loading countries...
                        </div>
                      ) : countries.length > 0 ? (
                        countries.map(country => (
                          <div
                            key={country.id}
                            className="dropdown-item country-item"
                            onClick={() => selectCountry(country)}
                          >
                            <span className="dial-code">{country.dial_code}</span>
                          </div>
                        ))
                      ) : (
                        <div className="dropdown-item no-results">
                          No countries found
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={formErrors.phone ? 'form-input error phone-input' : 'form-input phone-input'}
                  placeholder="Enter phone number"
                  required
                />
              </div>
              {formErrors.countryId && (
                <div className="error-message">{formErrors.countryId}</div>
              )}
              {formErrors.phone && (
                <div className="error-message">{formErrors.phone}</div>
              )}
            </div>

            <div className="form-row">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={prevStep}
              >
                Previous
              </button>
              <button type="button" className="btn" onClick={nextStep}>
                Next Step
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="form-section active">
            <h3>Security Setup</h3>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="password">Password *</label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={formErrors.password ? 'form-input error' : 'form-input'}
                    placeholder="Create a password"
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label="Toggle password visibility"
                  >
                    <i className={showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
                  </button>
                </div>
                {formErrors.password && (
                  <div className="error-message">{formErrors.password}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password *</label>
                <div className="password-input-container">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={formErrors.confirmPassword ? 'form-input error' : 'form-input'}
                    placeholder="Confirm your password"
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label="Toggle confirm password visibility"
                  >
                    <i className={showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
                  </button>
                </div>
                {formErrors.confirmPassword && (
                  <div className="error-message">{formErrors.confirmPassword}</div>
                )}
              </div>
            </div>

            <div className="form-group" style={{ marginTop: '1.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  id="terms"
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleChange}
                  required
                />
                <span>
                  I agree to the{' '}
                  {legalLinks.terms_url ? (
                    <a href={legalLinks.terms_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-color)' }}>Terms of Service</a>
                  ) : (
                    <span>Terms of Service</span>
                  )}{' '}
                  and{' '}
                  {legalLinks.privacy_policy_url ? (
                    <a href={legalLinks.privacy_policy_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-color)' }}>Privacy Policy</a>
                  ) : (
                    <span>Privacy Policy</span>
                  )}
                </span>
              </label>
              {formErrors.agreeToTerms && (
                <div className="error-message">{formErrors.agreeToTerms}</div>
              )}
            </div>

            <div className="form-row">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={prevStep}
              >
                Previous
              </button>
              <button
                type="submit"
                className="btn"
                disabled={loading}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // ---------- main render ----------
  return (
    <div className="register-page">
      <Link to="/" className="back-link desktop-only">
        <i className="fas fa-arrow-left"></i> Back to Home
      </Link>

      <div className="register-container">
        <div className="register-left">
          <h2>Join eHAAZRI Today</h2>
          <p>Start your journey with smart attendance management</p>
          <ul className="features-list">
            <li>
              <i className="fas fa-check"></i>
              <span>4 User licenses free for lifetime</span>
            </li>
            <li>
              <i className="fas fa-map-marker-alt"></i>
              <span>Advanced geofencing</span>
            </li>
            <li>
              <i className="fas fa-user-shield"></i>
              <span>Facial recognition</span>
            </li>
            <li>
              <i className="fas fa-chart-line"></i>
              <span>Real-time analytics</span>
            </li>
            <li>
              <i className="fas fa-headset"></i>
              <span>24/7 support</span>
            </li>
          </ul>
        </div>

        <div className="register-right">
          <Logo size="medium" usePublicLogo={true} />

          <div className="form-steps">
            {[1, 2, 3].map((step, index) => (
              <React.Fragment key={step}>
                <div className="step">
                  <div className={`step-number ${currentStep >= step ? 'active' : ''} ${currentStep > step ? 'completed' : ''}`}>
                    {currentStep > step ? <i className="fas fa-check"></i> : step}
                  </div>
                </div>
                {index < 2 && (
                  <div className={`step-line ${currentStep > step ? 'completed' : ''}`}></div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Conditional rendering: form / verification / success */}
          {registrationStep === 'form' && (
            <form className="register-form" onSubmit={handleSubmit}>
              {error && (
                <div className="error-message global-error">
                  <i className="fas fa-exclamation-circle"></i>
                  {error}
                </div>
              )}

              {formErrors.general && (
                <div className="error-message global-error">
                  <i className="fas fa-exclamation-circle"></i>
                  {formErrors.general}
                </div>
              )}

              {renderStepContent()}
            </form>
          )}

          {registrationStep === 'verification' && (
            <div className="verification-container">
              <div className="verification-content">
                <div className="verification-icon">
                  <i className="fas fa-envelope-open-text"></i>
                </div>
                <h2>Check Your Email</h2>
                <p>
                  We've sent a verification email to <strong>{formData.email || localStorage.getItem('registrationEmail')}</strong>
                </p>
                <p>
                  Please click the verification link in the email to activate your account.
                  The link will expire in 24 hours.
                </p>

                <div className="verification-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleResendVerification}
                  >
                    <i className="fas fa-redo"></i>
                    Resend Verification Email
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleGoToLogin}
                  >
                    <i className="fas fa-sign-in-alt"></i>
                    Go to Login
                  </button>
                </div>

                <div className="help-text">
                  <p>Didn't receive the email?</p>
                  <ul>
                    <li>Check your spam/junk folder</li>
                    <li>Make sure you entered the correct email address</li>
                    <li>Contact support if you continue having issues</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {registrationStep === 'success' && (
            <div className="verification-container">
              <div className="verification-content">
                <div className="verification-icon">
                  <i className="fas fa-check-circle"></i>
                </div>
                <h2>Account Created</h2>
                <p>Your account was created successfully. You can now log in.</p>

                <div className="verification-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleGoToLogin}
                  >
                    <i className="fas fa-sign-in-alt"></i>
                    Go to Login
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="login-link">
            Already have an account?{' '}
            <Link to="/login">Sign In</Link>
          </div>

          <Link to="/" className="back-link mobile-bottom">
            <i className="fas fa-arrow-left"></i> Back to Home
          </Link>
        </div>
      </div>

      {/* Keep your CSS here (copied from original to preserve styles) */}
      <style>{`
        * { margin: 0; padding: 0; box-sizing: border-box; }
        :root {
          --primary-color: #00bcd4;
          --secondary-color: #ff5722;
          --dark-color: #2c3e50;
          --light-color: #ecf0f1;
          --white: #ffffff;
          --text-dark: #2c3e50;
          --text-light: #7f8c8d;
          --gradient: linear-gradient(135deg, #00bcd4 0%, #ff5722 100%);
          --success: #27ae60;
          --error: #e74c3c;
        }
        .register-page { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background: var(--gradient); min-height: 100vh; height: 100%; display:flex; align-items:center; justify-content:center; padding:20px; position:relative; flex: 1; }
        .back-link.desktop-only { position:absolute; top:20px; left:20px; color:var(--white); text-decoration:none; display:flex; align-items:center; gap:5px; font-weight:500; transition:opacity .3s; }
        .register-container { background: var(--white); border-radius:20px; box-shadow:0 20px 40px rgba(0,0,0,.1); overflow:hidden; max-width:900px; width:100%; display:grid; grid-template-columns:1fr 1fr; min-height:600px; }
        .register-left { background:var(--gradient); color:var(--white); padding:3rem; display:flex; flex-direction:column; justify-content:center; text-align:center; }
        .register-left h2 { font-size:2rem; margin-bottom:1rem; font-weight:700; }
        .features-list { text-align:left; list-style:none; }
        .features-list li { margin-bottom:1rem; display:flex; align-items:center; gap:10px; }
        .features-list li i { color:var(--white); background: rgba(255,255,255,0.2); padding:8px; border-radius:50%; width:35px; height:35px; display:flex; align-items:center; justify-content:center; }
        .register-right { padding:3rem; display:flex; flex-direction:column; justify-content:center; }
        .logo { display:flex; align-items:center; gap:10px; margin-bottom:2rem; justify-content:center; }
        .logo-icon { width:40px; height:40px; background:var(--gradient); border-radius:8px; display:flex; align-items:center; justify-content:center; color:white; font-weight:bold; }
        .logo-text { font-size:1.5rem; font-weight:bold; background:var(--gradient); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; }
        .form-steps { display:flex; justify-content:center; margin-bottom:2rem; align-items:center; }
        .step { display:flex; align-items:center; gap:10px; }
        .step-number { width:30px; height:30px; border-radius:50%; background:#e0e0e0; color:var(--text-light); display:flex; align-items:center; justify-content:center; font-weight:bold; font-size:.9rem; transition:all .3s ease; }
        .step-number.active { background:var(--primary-color); color:var(--white); }
        .step-number.completed { background:var(--success); color:var(--white); }
        .step-line { width:50px; height:2px; background:#e0e0e0; transition:all .3s ease; }
        .step-line.completed { background:var(--success); }
        .register-form h3 { color:var(--text-dark); margin-bottom:2rem; text-align:center; font-size:1.5rem; }
        .form-section { display:block; }
        .form-row { display:grid; grid-template-columns:1fr 1fr; gap:1rem; margin-bottom:1rem; }
        .form-group { margin-bottom:1rem; position:relative; }
        .form-group label { display:block; margin-bottom:.5rem; color:var(--text-dark); font-weight:500; }
        .form-input { width:100%; padding:.8rem; border:2px solid #e0e0e0; border-radius:8px; font-size:1rem; transition:all .3s; outline:none; }
        .form-input:focus { border-color:var(--primary-color); box-shadow:0 0 0 3px rgba(0,188,212,0.1); }
        .form-input.error { border-color:var(--error); }
        textarea.form-input { resize:vertical; min-height:80px; }
        .dropdown-container { position:relative; }
        .dropdown-menu { position:absolute; top:100%; left:0; right:0; background:white; border:2px solid #e0e0e0; border-radius:8px; box-shadow:0 10px 25px rgba(0,0,0,0.1); max-height:200px; overflow-y:auto; z-index:1000; margin-top:.25rem; }
        .dropdown-item { padding:.8rem; cursor:pointer; border-bottom:1px solid #f0f0f0; transition:background-color .2s; }
        .dropdown-item:hover { background-color:#f8f9fa; }
        .dropdown-item:last-child { border-bottom:none; }
        .dropdown-item.loading { display:flex; align-items:center; gap:.5rem; cursor:default; color:var(--text-light); }
        .dropdown-item.no-results { color:var(--text-light); font-style:italic; cursor:default; text-align:center; }
        .country-item { display:flex; align-items:center; gap:.5rem; }
        .country-flag { font-weight:bold; color:var(--primary-color); min-width:30px; }
        .country-name { flex:1; }
        .dial-code { color:var(--text-light); font-weight:600; font-size:.9rem; }
        .phone-input-container { display:flex; gap:.5rem; }
        .country-selector { position:relative; min-width:150px; max-width:150px; }
        .country-input { width:100%; }
        .phone-input { flex:2; }
        .password-input-container { position:relative; }
        .password-toggle { position:absolute; right:.8rem; top:50%; transform:translateY(-50%); background:none; border:none; color:var(--text-light); cursor:pointer; padding:.3rem; border-radius:4px; transition:color .2s; }
        .password-toggle:hover { color:var(--primary-color); }
        .btn { width:100%; padding:1rem; background:var(--gradient); color:var(--white); border:none; border-radius:8px; font-size:1rem; font-weight:600; cursor:pointer; transition:all .3s; margin-top:1rem; display:flex; align-items:center; justify-content:center; gap:.5rem; }
        .btn:hover:not(:disabled) { transform:translateY(-2px); box-shadow:0 10px 20px rgba(0,0,0,0.2); }
        .btn:disabled { opacity:.6; cursor:not-allowed; transform:none; }
        .btn-secondary { background:#95a5a6; }
        .btn-secondary:hover { background:#7f8c8d; }
        .error-message { color:var(--error); font-size:.9rem; margin-top:.5rem; display:block; }
        .global-error { background: rgba(231,76,60,0.1); padding:1rem; border-radius:8px; margin-bottom:1.5rem; display:flex; align-items:center; gap:.5rem; border:1px solid rgba(231,76,60,0.2); }
        .login-link { text-align:center; margin-top:1rem; color:var(--text-light); }
        .login-link a { color:var(--primary-color); text-decoration:none; font-weight:600; }
        .login-link a:hover { text-decoration:underline; }
        .back-link.mobile-bottom { display:none; justify-content:center; align-items:center; gap:.5rem; color:var(--text-light); text-decoration:none; font-weight:500; margin-top:1rem; padding:.8rem; border-radius:8px; transition:all .3s; }
        .back-link.mobile-bottom:hover { color:var(--primary-color); background:rgba(52,152,219,0.1); }
        .spinner { width:16px; height:16px; border:2px solid rgba(255,255,255,0.3); border-top:2px solid currentColor; border-radius:50%; animation:spin 1s linear infinite; }
        @keyframes spin { 0%{ transform:rotate(0deg);} 100%{ transform:rotate(360deg);} }
        .verification-container { max-width:600px; margin:2rem auto; background:var(--white); border-radius:20px; box-shadow:0 25px 50px rgba(0,0,0,0.15); overflow:hidden; }
        .verification-content { padding:3rem; text-align:center; }
        .verification-icon { width:80px; height:80px; background:var(--gradient); border-radius:50%; display:flex; align-items:center; justify-content:center; margin:0 auto 2rem; font-size:2rem; color:white; }
        .verification-content h2 { color:var(--text-dark); margin-bottom:1.5rem; font-size:1.8rem; font-weight:600; }
        .verification-content p { color:var(--text-light); line-height:1.6; margin-bottom:1.5rem; font-size:1rem; }
        .verification-actions { display:flex; gap:1rem; justify-content:center; margin:2.5rem 0; flex-wrap:wrap; }
        .verification-actions .btn { width:auto; padding:1rem 2rem; margin:0; }
        .btn-primary { background:var(--gradient); }
        .help-text { background:#f8f9fa; padding:1.5rem; border-radius:12px; margin-top:2rem; text-align:left; border:1px solid #e0e6ed; }
        .help-text p { font-weight:600; color:var(--text-dark); margin-bottom:1rem; }
        .help-text ul { margin:0; padding-left:1.5rem; color:var(--text-light); }
        .help-text li { margin-bottom:.5rem; line-height:1.4; }
        @media (max-width:1024px) {
          .register-container { max-width:600px; margin:1rem; }
          .back-link.desktop-only { display:flex; }
        }
        @media (max-width:768px) {
          .register-page { padding:10px; }
          .register-container { grid-template-columns:1fr; max-width:500px; margin:0 auto; }
          .register-left { display:none; }
          .form-row { grid-template-columns:1fr; gap:1rem; }
          .phone-input-container { flex-direction:column; gap:1rem; }
          .country-selector { min-width:auto; }
          .back-link.desktop-only { display:none; }
          .back-link.mobile-bottom { display:flex; }
          .register-right { padding:2rem 1.5rem; }
          .form-steps { margin-bottom:1.5rem; }
          .step-line { width:30px; }
          .btn { padding:1rem; font-size:1rem; }
          .verification-content { padding:2rem; }
          .verification-actions { flex-direction:column; }
          .verification-actions .btn { width:100%; }
        }
        @media (max-width:480px) {
          .register-right { padding:1.5rem 1rem; }
          .form-steps { flex-direction:column; align-items:center; gap:.5rem; }
          .step-line { display:none; }
          .form-input { padding:.8rem; font-size:16px; }
        }
      `}</style>
    </div>
  );
};

export default Register;
